import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import Close from "@mui/icons-material/Close";
import Download from "@mui/icons-material/Download";
import Refresh from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { WasteInvoiceImportRunQuery, WasteInvoiceImportStatus } from "generated-graphql/graphql";
import { useCallback, useEffect, useRef, useState } from "react";
import { prettyPrintDateTimeMed } from "util/dates";

import {
  QUEUE_WASTE_INVOICE_IMPORT,
  WASTE_INVOICE_IMPORT_RUN_QUERY,
  WASTE_INVOICES_QUERY,
} from "../queries";
import { formatInvoiceMoney, IMPORT_VENDOR_LABELS } from "../utils";
import { InvoiceImportStatusChip } from "./InvoiceImportStatusChip";

const ACTIVE_STATUSES = new Set<WasteInvoiceImportStatus>([
  WasteInvoiceImportStatus.Queued,
  WasteInvoiceImportStatus.Running,
]);

type ImportRun = NonNullable<WasteInvoiceImportRunQuery["wasteInvoiceImportRun"]>;

function LoadingStatus() {
  return (
    <Card sx={{ mx: 3, mt: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading invoice import…</Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

function StatusLoadError({
  runId,
  onClose,
  onRetry,
}: {
  runId: string;
  onClose: () => void;
  onRetry: () => void;
}) {
  return (
    <Alert
      severity="warning"
      sx={{ mx: 3, mt: 3 }}
      action={
        <Stack direction="row" spacing={1}>
          <Button onClick={onRetry}>Check again</Button>
          <Button onClick={onClose}>Dismiss</Button>
        </Stack>
      }
    >
      We could not refresh this import's status. The server import may still be running. Run {runId}
      .
    </Alert>
  );
}

function RunStateMessage({ status }: { status: WasteInvoiceImportStatus }) {
  if (status === WasteInvoiceImportStatus.Queued)
    return <Alert severity="info">Your workbook is queued.</Alert>;
  if (status === WasteInvoiceImportStatus.Running) {
    return <Alert severity="info">Your workbook is being validated and imported.</Alert>;
  }
  if (status === WasteInvoiceImportStatus.Cancelled) {
    return (
      <Alert severity="warning">
        This import was cancelled before completion. No completed import summary is available.
      </Alert>
    );
  }
  return null;
}

function ImportSummary({ run, onClose }: { run: ImportRun; onClose: () => void }) {
  if (!run.summary) return null;
  return (
    <Stack spacing={1} alignItems="flex-start">
      <Alert severity="success" sx={{ width: "100%" }}>
        Read {run.summary.sourceRowCount.toLocaleString()} source rows and imported{" "}
        {run.summary.invoiceCount.toLocaleString()} invoices with{" "}
        {run.summary.lineCount.toLocaleString()} lines totaling{" "}
        {formatInvoiceMoney(run.summary.totalBilledAmount)}. {run.summary.createdCount} new,{" "}
        {run.summary.noOpCount} unchanged. Completed{" "}
        {prettyPrintDateTimeMed(run.summary.completedAt)}.
      </Alert>
      <Button size="small" onClick={onClose}>
        View invoice table
      </Button>
    </Stack>
  );
}

function ImportFailure({
  run,
  getValidationArtifactDownloadLink,
}: {
  run: ImportRun;
  getValidationArtifactDownloadLink: () => Promise<string | null>;
}) {
  const [showIssues, setShowIssues] = useState(false);
  const [downloadingArtifact, setDownloadingArtifact] = useState(false);
  const [artifactDownloadError, setArtifactDownloadError] = useState<string | null>(null);
  if (!run.failure) return null;
  const { failure } = run;
  const downloadValidationArtifact = async () => {
    setArtifactDownloadError(null);
    setDownloadingArtifact(true);
    try {
      const downloadLink = await getValidationArtifactDownloadLink();
      if (!downloadLink) {
        throw new Error("Unable to generate a fresh validation report link.");
      }
      window.open(downloadLink, "_blank", "noopener");
    } catch (downloadError) {
      setArtifactDownloadError(
        downloadError instanceof Error
          ? downloadError.message
          : "Unable to download the validation report."
      );
    } finally {
      setDownloadingArtifact(false);
    }
  };
  const issuePrefix = (issue: (typeof failure.issues)[number]) =>
    [issue.invoiceNumber, issue.sourceRowNumber && `row ${issue.sourceRowNumber}`, issue.field]
      .filter(Boolean)
      .join(" · ");
  return (
    <Stack spacing={1}>
      <Alert severity="error">{failure.message}</Alert>
      {failure.totalIssueCount != null && failure.issues.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          Showing {failure.issues.length.toLocaleString()} of{" "}
          {failure.totalIssueCount.toLocaleString()} issues.
        </Typography>
      )}
      <Stack direction="row" spacing={1}>
        {failure.issues.length > 0 && (
          <Button size="small" onClick={() => setShowIssues((value) => !value)}>
            {showIssues ? "Hide" : "Show"} validation issues
            {failure.totalIssueCount != null
              ? ` (${failure.totalIssueCount.toLocaleString()})`
              : ""}
          </Button>
        )}
        {failure.validationArtifactDownloadLink && (
          <Button
            size="small"
            startIcon={<Download />}
            loading={downloadingArtifact}
            onClick={downloadValidationArtifact}
          >
            Download validation report
          </Button>
        )}
      </Stack>
      {artifactDownloadError && <Alert severity="error">{artifactDownloadError}</Alert>}
      <Collapse in={showIssues}>
        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 3 }}>
          {failure.issues.map((issue, index) => {
            const prefix = issuePrefix(issue);
            return (
              <Typography component="li" variant="body2" key={`${issue.code}-${index}`}>
                {prefix}
                {prefix ? ": " : ""}
                {issue.message}
              </Typography>
            );
          })}
        </Stack>
      </Collapse>
    </Stack>
  );
}

function ImportRunCard({
  run,
  retrying,
  retryError,
  canImport,
  getValidationArtifactDownloadLink,
  onClose,
  onRetry,
  onChooseAnother,
}: {
  run: ImportRun;
  retrying: boolean;
  retryError: string | null;
  canImport: boolean;
  getValidationArtifactDownloadLink: () => Promise<string | null>;
  onClose: () => void;
  onRetry: () => void;
  onChooseAnother: () => void;
}) {
  const active = ACTIVE_STATUSES.has(run.status);
  const canRetry =
    canImport &&
    (run.status === WasteInvoiceImportStatus.Failed ||
      run.status === WasteInvoiceImportStatus.Cancelled);
  return (
    <Card sx={{ mx: 3, mt: 3 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>
                  {run.sourceDocument.title}
                </Typography>
                <InvoiceImportStatusChip status={run.status} />
                {active && <CircularProgress size={16} />}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {IMPORT_VENDOR_LABELS[run.vendor]} invoice import
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Run {run.id}
              </Typography>
            </Box>
            <IconButton aria-label="Dismiss import status" size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
          <RunStateMessage status={run.status} />
          <ImportSummary run={run} onClose={onClose} />
          <ImportFailure
            run={run}
            getValidationArtifactDownloadLink={getValidationArtifactDownloadLink}
          />
          {retryError && <Alert severity="error">{retryError}</Alert>}
          {canRetry && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                disabled={retrying}
                onClick={onRetry}
              >
                Retry with uploaded document
              </Button>
              <Button size="small" onClick={onChooseAnother}>
                Choose another file
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export function InvoiceImportStatus({
  runId,
  canImport,
  onClose,
  onRunChanged,
  onChooseAnother,
}: {
  runId: string;
  canImport: boolean;
  onClose: () => void;
  onRunChanged: (runId: string) => void;
  onChooseAnother: () => void;
}) {
  const client = useApolloClient();
  const [retryError, setRetryError] = useState<string | null>(null);
  const previousStatus = useRef<WasteInvoiceImportStatus | null>(null);
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(
    WASTE_INVOICE_IMPORT_RUN_QUERY,
    {
      variables: { id: runId },
      fetchPolicy: "network-only",
    }
  );
  const [queueImport, { loading: retrying }] = useMutation(QUEUE_WASTE_INVOICE_IMPORT);
  const run = data?.wasteInvoiceImportRun;

  useEffect(() => {
    if (run && ACTIVE_STATUSES.has(run.status)) {
      startPolling(3000);
      return stopPolling;
    }
    stopPolling();
    return undefined;
  }, [run, startPolling, stopPolling]);

  useEffect(() => {
    if (
      run?.status === WasteInvoiceImportStatus.Succeeded &&
      previousStatus.current !== WasteInvoiceImportStatus.Succeeded
    ) {
      client.refetchQueries({ include: [WASTE_INVOICES_QUERY] });
    }
    previousStatus.current = run?.status ?? null;
  }, [client, run?.status]);

  const handleRetry = useCallback(async () => {
    if (!run) return;
    setRetryError(null);
    try {
      const result = await queueImport({
        variables: {
          input: {
            tenantId: run.tenantId,
            sourceDocumentId: run.sourceDocument.id,
            vendor: run.vendor,
          },
        },
      });
      const nextRun = result.data?.queueWasteInvoiceImport;
      if (!nextRun) throw new Error("The retry did not return an import run.");
      onRunChanged(nextRun.id);
    } catch (retryFailure) {
      setRetryError(
        retryFailure instanceof Error ? retryFailure.message : "Unable to retry the import."
      );
    }
  }, [onRunChanged, queueImport, run]);

  const getValidationArtifactDownloadLink = useCallback(async () => {
    const result = await refetch();
    return result.data?.wasteInvoiceImportRun.failure?.validationArtifactDownloadLink ?? null;
  }, [refetch]);

  if (loading && !run) {
    return <LoadingStatus />;
  }

  if (error || !run) {
    return <StatusLoadError runId={runId} onClose={onClose} onRetry={() => refetch()} />;
  }
  return (
    <ImportRunCard
      run={run}
      retrying={retrying}
      retryError={retryError}
      canImport={canImport}
      getValidationArtifactDownloadLink={getValidationArtifactDownloadLink}
      onClose={onClose}
      onRetry={handleRetry}
      onChooseAnother={onChooseAnother}
    />
  );
}
